// Trigger-label collision layout — the pure half of App.jsx's computeVisibility.
//
// A label is a fixed-width box centred on its circle's x, on one side of the spine.
// Two labels collide only when they are on the SAME side and their boxes overlap
// horizontally by more than a small threshold; opposite sides never collide (they are
// vertically separated by the spine). This module answers two questions the layout
// loop asks for every label, in priority order:
//
//   * does this box fit here?            — `createLabelOccupancy(...).fits`
//   * which side should it try first?    — `sideCandidates`
//
// SIDE CHOICE is where the density comes from. A VALENCED trigger's label is bound to
// its circle's side (YES above, NO below). A NEUTRAL one sits on the spine, so both
// sides are open — and choosing by room rather than by a fixed alternation fits more
// labels before any has to collapse to a dot: a label goes to whichever side has more
// clearance from its nearest neighbour, so crowded neighbours stagger instead of
// stacking on the side an index happened to pick.

/**
 * A running record of the label boxes already placed on one row.
 *
 * @param {{labelW: number, thresh: number, looseExtra?: number, enterGap?: number,
 *   wallX?: number|null}} opts
 *   `thresh` is the horizontal overlap (px) tolerated before two boxes collide;
 *   `looseExtra` widens it for a label that was shown last pass, so a label already on
 *   screen does not flicker off over a pixel of pan. `enterGap` is the clear space a
 *   label that was a DOT last pass needs before it opens again. Together they make a
 *   dead band: a label near the boundary stays in whichever state it is in instead of
 *   toggling on every zoom frame.
 *
 *   `wallX` is a right edge no box may cross, on either side: the forecast's outcome bars.
 *   Unlike a neighbour it gets no overlap tolerance, loose or otherwise — a label under the
 *   bars is hidden by them, not merely crowded — but it keeps the `enterGap` dead band.
 */
export function createLabelOccupancy({
  labelW,
  thresh,
  looseExtra = 12,
  enterGap = 16,
  wallX = null,
}) {
  const boxes = [];
  const hw = labelW / 2;
  return {
    /** Does a box centred at `sx` on `above` fit without colliding? `mode` is `true` /
     *  `"loose"` for a label shown last pass, `"strict"` for one hidden last pass. */
    fits(sx, above, mode = false) {
      if (wallX != null && sx + hw > wallX - (mode === "strict" ? enterGap : 0)) return false;
      const t =
        mode === true || mode === "loose"
          ? thresh + looseExtra
          : mode === "strict"
            ? -enterGap
            : thresh;
      for (const o of boxes) {
        if (o.above !== above) continue;
        if (Math.min(sx + hw, o.right) - Math.max(sx - hw, o.left) > t) return false;
      }
      return true;
    },
    /** Record a box centred at `sx` on `above`. */
    place(sx, above) {
      boxes.push({ left: sx - hw, right: sx + hw, above });
    },
    /** Clear space (px) between a box at `sx` and its nearest neighbour on `above`;
     *  negative when they overlap, Infinity when that side is empty. */
    gap(sx, above) {
      let best = Infinity;
      for (const o of boxes) {
        if (o.above !== above) continue;
        const g = sx < o.left ? o.left - (sx + hw) : sx > o.right ? sx - hw - o.right : -labelW;
        if (g < best) best = g;
      }
      return best;
    },
  };
}

/**
 * The sides to try for one label, best first.
 *
 * @param {object} a
 * @param {boolean} a.neutral     sits on the spine, so either side is allowed
 * @param {boolean} a.fixedAbove  the side a VALENCED label must use
 * @param {boolean} a.preferAbove the neutral tie-break (an alternation seed)
 * @param {boolean|null} a.lastAbove the side it was shown on last pass, or null —
 *   kept first so a label does not hop sides while the user pans
 * @param {ReturnType<typeof createLabelOccupancy>} a.occ
 * @param {number} a.sx
 * @returns {boolean[]} `true` = above
 */
export function sideCandidates({ neutral, fixedAbove, preferAbove, lastAbove, occ, sx }) {
  if (!neutral) return [fixedAbove];
  if (lastAbove === true || lastAbove === false) return [lastAbove, !lastAbove];
  const up = occ.gap(sx, true);
  const down = occ.gap(sx, false);
  if (up === down) return [preferAbove, !preferAbove];
  return up > down ? [true, false] : [false, true];
}

/**
 * How one label is placed when the layout re-runs.
 *
 * With NOTHING geometric changed — no zoom, no pan — which is what a click does: the layout
 * is greedy, so re-running it from scratch after a click lets one bumped label slide or
 * change side and bump the next, and lets hidden labels pop on in the space that frees: a
 * click changed labels nowhere near it. With the geometry unchanged, every label keeps its
 * previous state instead, and the only changes are the ones the click forces.
 *
 * When only the zoom changed, and it zoomed OUT, a dot stays a dot: zooming out only takes
 * room away, so it may close labels but never open one.
 *
 * - `"layout"` — lay it out normally: the geometry changed (so a full re-layout is due),
 *   it is the newest opened label (a click must open it), or the row has not seen it before.
 * - `"keep"` — it was shown: show it again on the same side if that still fits, else close
 *   it to a dot (it overlaps the newly opened label). Never slid, never moved across.
 * - `"hide"` — it was a dot: it stays one.
 *
 * @param {object} a
 * @param {boolean} a.sameGeometry  zoom and pan are what they were on this row's last pass
 * @param {boolean} [a.zoomedOut]   only the zoom changed since that pass, and it went down
 * @param {boolean} a.isNewest      the most recently opened label
 * @param {boolean} a.seen          this row's last pass laid this label out
 * @param {boolean} a.wasShown      ...and showed it (not a dot)
 * @returns {"layout"|"keep"|"hide"}
 */
export function stickyLabelMode({ sameGeometry, zoomedOut = false, isNewest, seen, wasShown }) {
  if (isNewest || !seen) return "layout";
  if (sameGeometry) return wasShown ? "keep" : "hide";
  return zoomedOut && !wasShown ? "hide" : "layout";
}

/**
 * The order the overlap pass places labels in; an earlier label wins any collision.
 *
 * 1. The branch fork trigger — it owns its slot.
 * 2. Open (clicked) labels, newest click first, so opening a label closes the older open
 *    ones it overlaps.
 * 3. Labels that were dots last pass go after the rest, so a re-layout never opens a dot on
 *    top of a label already on screen — it opens only into room that is actually free. A
 *    label the row has not seen before is new data, not a dot, and competes on relevance.
 * 4. Relevance, then left to right.
 *
 * @param {Array<{isFork: boolean, promotedRank: number, wasDot: boolean,
 *   relevance: number, sx: number}>} items
 * @returns {number[]} indices into `items`, first placed first
 */
export function labelPlacementOrder(items) {
  return [...items.keys()].sort((a, b) => {
    const p = items[a];
    const q = items[b];
    if (p.isFork !== q.isFork) return p.isFork ? -1 : 1;
    if (p.promotedRank !== q.promotedRank) return q.promotedRank - p.promotedRank;
    if (p.wasDot !== q.wasDot) return p.wasDot ? 1 : -1;
    if (Math.abs(p.relevance - q.relevance) > 0.01) return q.relevance - p.relevance;
    return p.sx - q.sx;
  });
}

/** Zoom steps per doubling: one step is ~9% of zoom. */
export const ZOOM_STEPS_PER_OCTAVE = 8;

/**
 * The zoom step `ppd` (px per day) falls in — the layout's geometry key. Labels are only
 * re-arranged when this changes, so a zoom gesture re-lays them out a handful of times
 * rather than on every frame. Steps compare numerically: a higher step is zoomed further in.
 *
 * @param {number} ppd
 * @returns {string}
 */
export function zoomStep(ppd) {
  return Number.isFinite(ppd) && ppd > 0
    ? String(Math.round(Math.log2(ppd) * ZOOM_STEPS_PER_OCTAVE))
    : "none";
}

/** How long a closed label's ghost takes to fade out (ms); matches the label fade-in. */
export const LABEL_FADE_MS = 180;

/**
 * Track the labels that just closed, so the row can fade each one out over its dot instead
 * of removing it in one frame. Mutates `ghosts` (key → {start, above}) and reports which
 * ghosts to draw this frame.
 *
 * - A label shown last pass and closed now starts a ghost on the side it was shown on.
 * - A label shown now drops any ghost it had (it reopened).
 * - A ghost older than `fadeMs` is dropped.
 *
 * @param {Map<string, {start: number, above: boolean}>} ghosts persisted across renders
 * @param {Array<{key: string, shown: boolean, lastAbove: boolean|undefined}>} labels
 *   this row's labels: `lastAbove` is the side it was shown on last pass, or undefined
 *   when it was a dot
 * @param {number} now ms clock
 * @param {number} [fadeMs]
 * @returns {{draw: Map<string, boolean>, started: boolean}} `draw` is key → side for the
 *   ghosts to render; `started` says a ghost began, so the caller schedules the frame that
 *   clears it once the fade ends
 */
export function updateLabelGhosts(ghosts, labels, now, fadeMs = LABEL_FADE_MS) {
  const draw = new Map();
  let started = false;
  for (const { key, shown, lastAbove } of labels) {
    if (shown) {
      ghosts.delete(key);
      continue;
    }
    if (lastAbove !== undefined && !ghosts.has(key)) {
      ghosts.set(key, { start: now, above: lastAbove });
      started = true;
    }
    const g = ghosts.get(key);
    if (!g) continue;
    if (now - g.start >= fadeMs) ghosts.delete(key);
    else draw.set(key, g.above);
  }
  return { draw, started };
}
